import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4.css';
import './Homework9.css';

type Tab = 'otsu' | 'region-growing';

const Homework9: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('otsu');
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedPreset, setSelectedPreset] = useState<number>(1);

  // OTSU state
  const [otsuThreshold, setOtsuThreshold] = useState<number | null>(null);
  const [otsuBinaryImage, setOtsuBinaryImage] = useState<string | null>(null);
  const [otsuHistogram, setOtsuHistogram] = useState<{values: number[], threshold: number} | null>(null);

  // Region Growing state
  const [rgThreshold, setRgThreshold] = useState(10);
  const [rgSeedPoint, setRgSeedPoint] = useState<{x: number, y: number} | null>(null);
  const [rgSegmentedImage, setRgSegmentedImage] = useState<string | null>(null);
  const [rgMaskImage, setRgMaskImage] = useState<string | null>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState<{width: number, height: number} | null>(null);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setOriginalImage(result);
        setSelectedPreset(0); // Clear preset selection when custom image uploaded

        // Get natural size of the image
        const img = new Image();
        img.onload = () => {
          setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = result;

        clearResults();
      };
      reader.readAsDataURL(file);
    }
  };

  const clearResults = () => {
    setOtsuThreshold(null);
    setOtsuBinaryImage(null);
    setOtsuHistogram(null);
    setRgSeedPoint(null);
    setRgSegmentedImage(null);
    setRgMaskImage(null);
  };

  const loadPresetImage = async (imageNum: number) => {
    const imagePath = `/hw9_images/HW9_${imageNum}.png`;

    try {
      // Fetch the image and convert to base64
      const response = await fetch(imagePath);
      const blob = await response.blob();

      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        setOriginalImage(base64String);
        setSelectedPreset(imageNum);

        // Get natural size
        const img = new Image();
        img.onload = () => {
          setImageNaturalSize({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.src = base64String;

        clearResults();
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error loading preset image:', error);
      alert('Error loading preset image');
    }
  };

  // Auto-load default image on component mount
  useEffect(() => {
    loadPresetImage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processOtsu = async () => {
    if (!originalImage) {
      alert('Please upload an image first!');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework9',
        {
          operation: 'otsu',
          image: originalImage
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 60000
        }
      );

      if (response.data.success) {
        setOtsuThreshold(response.data.threshold);
        setOtsuBinaryImage(response.data.binary_image);
        setOtsuHistogram(response.data.histogram);
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (error: any) {
      console.error('Error processing OTSU:', error);
      alert('Error processing image: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!imageNaturalSize) return;

    const img = event.currentTarget;
    const rect = img.getBoundingClientRect();
    const displayedWidth = rect.width;
    const displayedHeight = rect.height;

    // Get click coordinates relative to displayed image
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;

    // Calculate scale factors
    const scaleX = imageNaturalSize.width / displayedWidth;
    const scaleY = imageNaturalSize.height / displayedHeight;

    // Convert to natural image coordinates
    const naturalX = Math.round(clickX * scaleX);
    const naturalY = Math.round(clickY * scaleY);

    setRgSeedPoint({ x: naturalX, y: naturalY });
  };

  const processRegionGrowing = async () => {
    if (!originalImage) {
      alert('Please upload an image first!');
      return;
    }

    if (!rgSeedPoint) {
      alert('Please click on the image to select a seed point!');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework9',
        {
          operation: 'region_growing',
          image: originalImage,
          seed_x: rgSeedPoint.x,
          seed_y: rgSeedPoint.y,
          threshold: rgThreshold
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 60000
        }
      );

      if (response.data.success) {
        setRgSegmentedImage(response.data.segmented_image);
        setRgMaskImage(response.data.mask_image);
      } else {
        alert('Error: ' + response.data.error);
      }
    } catch (error: any) {
      console.error('Error processing Region Growing:', error);
      alert('Error processing image: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessing(false);
    }
  };

  const downloadImage = (imageData: string, filename: string) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename;
    link.click();
  };

  return (
    <div className="homework4-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
        <div className="shape shape-3"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/" className="back-button">
          ← Back to Hub
        </Link>

        <header className="homework4-header">
          <div className="badge">Homework 9</div>
          <h1>Image Segmentation</h1>
          <p className="subtitle">OTSU Thresholding and Region Growing</p>
        </header>

      {/* Tabs */}
      <div className="hw9-tabs">
        <button
          className={`hw9-tab ${activeTab === 'otsu' ? 'active' : ''}`}
          onClick={() => setActiveTab('otsu')}
        >
          <div className="tab-content">
            <span className="tab-icon">🎯</span>
            <span className="tab-title">OTSU Method</span>
            <span className="tab-subtitle">Automatic Thresholding</span>
          </div>
        </button>
        <button
          className={`hw9-tab ${activeTab === 'region-growing' ? 'active' : ''}`}
          onClick={() => setActiveTab('region-growing')}
        >
          <div className="tab-content">
            <span className="tab-icon">🌱</span>
            <span className="tab-title">Region Growing</span>
            <span className="tab-subtitle">Seed-based Segmentation</span>
          </div>
        </button>
      </div>

      {/* Image Upload Section */}
      <div className="controls-section">
        <h3>Select or Upload Image</h3>

        <div className="image-selection-row">
          <div className="preset-selector">
            <label>Sample Images:</label>
            <select
              value={selectedPreset}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val > 0) loadPresetImage(val);
              }}
              className="preset-dropdown"
            >
              <option value={0}>-- Custom Image --</option>
              <option value={1}>Lenna (Classic)</option>
              <option value={2}>IKC Building</option>
              <option value={3}>AILAB Logo</option>
            </select>
          </div>

          <div className="custom-upload">
            <button onClick={() => fileInputRef.current?.click()} className="control-button">
              📁 Upload Custom
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {originalImage && (
          <div className="image-preview">
            <h4>Original Image</h4>
            <img src={originalImage} alt="Original" />
          </div>
        )}
      </div>

      {/* OTSU Tab Content */}
      {activeTab === 'otsu' && (
        <div className="tab-panel">
          <div className="controls-section">
            <h3>OTSU Method</h3>
            <p className="description">
              Automatically finds the optimal threshold value to separate foreground and background pixels.
            </p>
            <button
              onClick={processOtsu}
              disabled={processing || !originalImage}
              className="process-button"
            >
              {processing ? 'Processing...' : 'Apply OTSU'}
            </button>
          </div>

          {otsuThreshold !== null && (
            <div className="info-section">
              <h4>Optimal Threshold: {otsuThreshold}</h4>
            </div>
          )}

          <div className="results-section">
            <h3>Results</h3>
            <div className="hw9-results-grid">
              <div className="result-item">
                <h4>Binary Image</h4>
                {otsuBinaryImage ? (
                  <>
                    <img src={otsuBinaryImage} alt="Binary Result" />
                    <button
                      onClick={() => downloadImage(otsuBinaryImage, 'otsu_binary.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Binary result will appear here</p>
                  </div>
                )}
              </div>

              <div className="result-item">
                <h4>Histogram with Threshold</h4>
                {otsuHistogram ? (
                  <div className="histogram-container">
                    <canvas
                      ref={(canvas) => {
                        if (canvas && otsuHistogram) {
                          const ctx = canvas.getContext('2d');
                          if (ctx) {
                            canvas.width = 400;
                            canvas.height = 250;

                            // Clear canvas
                            ctx.clearRect(0, 0, canvas.width, canvas.height);

                            // Draw histogram
                            const maxVal = Math.max(...otsuHistogram.values);
                            const barWidth = canvas.width / 256;

                            ctx.fillStyle = '#3b82f6';
                            for (let i = 0; i < 256; i++) {
                              const barHeight = (otsuHistogram.values[i] / maxVal) * (canvas.height - 30);
                              ctx.fillRect(i * barWidth, canvas.height - barHeight - 20, barWidth, barHeight);
                            }

                            // Draw threshold line
                            ctx.strokeStyle = 'red';
                            ctx.lineWidth = 2;
                            const thresholdX = (otsuHistogram.threshold / 255) * canvas.width;
                            ctx.beginPath();
                            ctx.moveTo(thresholdX, 0);
                            ctx.lineTo(thresholdX, canvas.height - 20);
                            ctx.stroke();

                            // Draw label
                            ctx.fillStyle = 'black';
                            ctx.font = '12px Arial';
                            ctx.fillText(`Threshold: ${otsuHistogram.threshold}`, 10, canvas.height - 5);
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <div className="placeholder-box">
                    <p>Histogram will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Region Growing Tab Content */}
      {activeTab === 'region-growing' && (
        <div className="tab-panel">
          <div className="controls-section">
            <h3>Region Growing</h3>
            <p className="description">
              Click on the image to select a seed point, then adjust the threshold and click "Grow Region".
            </p>

            <div className="parameter-control">
              <label>
                Similarity Threshold: {rgThreshold}
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={rgThreshold}
                  onChange={(e) => setRgThreshold(Number(e.target.value))}
                  className="slider"
                />
              </label>
              <p className="parameter-description">
                Maximum pixel intensity difference to include in the region
              </p>
            </div>

            {rgSeedPoint && (
              <div className="info-section">
                <p>Seed Point: ({rgSeedPoint.x}, {rgSeedPoint.y})</p>
                <button
                  onClick={() => setRgSeedPoint(null)}
                  className="reset-button"
                >
                  Reset Seed Point
                </button>
              </div>
            )}

            <button
              onClick={processRegionGrowing}
              disabled={processing || !originalImage || !rgSeedPoint}
              className="process-button"
            >
              {processing ? 'Processing...' : 'Grow Region'}
            </button>
          </div>

          {originalImage && (
            <div className="seed-selection-section">
              <h4>Click on the image to select seed point</h4>
              <div className="clickable-image-container">
                <img
                  src={originalImage}
                  alt="Click to select seed"
                  onClick={handleImageClick}
                  style={{ cursor: 'crosshair' }}
                />
                {rgSeedPoint && imageNaturalSize && (
                  <div
                    className="seed-marker"
                    style={{
                      position: 'absolute',
                      left: `${(rgSeedPoint.x / imageNaturalSize.width) * 100}%`,
                      top: `${(rgSeedPoint.y / imageNaturalSize.height) * 100}%`,
                      transform: 'translate(-50%, -50%)',
                      width: '20px',
                      height: '20px',
                      border: '3px solid red',
                      borderRadius: '50%',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </div>
            </div>
          )}

          <div className="results-section">
            <h3>Results</h3>
            <div className="hw9-results-grid">
              <div className="result-item">
                <h4>Segmented Region</h4>
                {rgSegmentedImage ? (
                  <>
                    <img src={rgSegmentedImage} alt="Segmented" />
                    <button
                      onClick={() => downloadImage(rgSegmentedImage, 'region_growing_segmented.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Segmented region will appear here</p>
                  </div>
                )}
              </div>

              <div className="result-item">
                <h4>Region Mask</h4>
                {rgMaskImage ? (
                  <>
                    <img src={rgMaskImage} alt="Region Mask" />
                    <button
                      onClick={() => downloadImage(rgMaskImage, 'region_growing_mask.png')}
                      className="download-button"
                    >
                      Download
                    </button>
                  </>
                ) : (
                  <div className="placeholder-box">
                    <p>Region mask will appear here</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Homework9;
